/**
 * @fileoverview Pure helpers for the power bank lifecycle: resolving the
 * inventory type, validating an upload CSV, and rendering hold money.
 *
 * Nothing here touches the network or React so it can be unit tested on its
 * own. The power bank type id is never hardcoded: it is resolved by name from
 * whatever `inventoryTypes` the API returns.
 */

/** The InventoryItemType.name the API creates the power bank type with. */
export const POWER_BANK_TYPE_NAME = 'Power Banks'

/** The API refuses uploads bigger than this in one call. */
export const MAX_POWER_BANK_UNITS_PER_UPLOAD = 200

/** Serial numbers longer than this are almost certainly a wrong column. */
const MAX_SERIAL_LENGTH = 100

export const POWER_BANK_CSV_TEMPLATE =
  'serialNumber,quantity\nPB-0001,\nPB-0002,\n,5'

export const POWER_BANK_HOLD_STATUS = {
  PLACED: 'PLACED',
  RELEASED: 'RELEASED',
  CANCELLED: 'CANCELLED',
}

/**
 * Case- and whitespace-insensitive match on the inventory type name.
 * @param {?string} name
 * @returns {boolean}
 */
export const isPowerBankTypeName = name =>
  typeof name === 'string' &&
  name.trim().toLowerCase() === POWER_BANK_TYPE_NAME.toLowerCase()

/**
 * Finds the power bank inventory type id in a list of `{ id, name }` types
 * (the shape of `state.inventory.inventoryTypes` and of the pickup query's
 * `inventoryTypes.edges[].node`).
 * @param {Array<{id: string|number, name: string}>} types
 * @returns {?string} the id as a string, or null when the type is not there
 */
export const findPowerBankTypeId = types => {
  const match = (types || []).find(
    type => type && isPowerBankTypeName(type.name),
  )
  return match ? String(match.id) : null
}

/**
 * Same lookup for a `{ [id]: name }` hash, the shape PickupData builds.
 * @param {Object<string, string>} typesHash
 * @returns {?string}
 */
export const findPowerBankTypeIdInHash = typesHash =>
  findPowerBankTypeId(
    Object.keys(typesHash || {}).map(id => ({ id, name: typesHash[id] })),
  )

const isBlankRow = row =>
  !Object.values(row || {}).some(
    value =>
      value !== null && value !== undefined && String(value).trim() !== '',
  )

const cleanCell = value =>
  value === null || value === undefined ? '' : String(value).trim()

/**
 * Validates a parsed power bank CSV (Papa.parse with `header: true`).
 *
 * Accepted columns:
 *  - `serialNumber` the serial printed on the unit. One row = one unit.
 *  - `quantity` only for rows WITHOUT a serial: how many unnamed units to
 *    create. A row with a serial must leave it empty or 1, because a serial
 *    identifies exactly one physical unit. A row with neither is blank and
 *    skipped (Papa.parse keeps trailing empty lines).
 *
 * Errors follow the `Row N: ...` convention used by the locations import so
 * the modal can group them by row.
 *
 * @param {{data: Array<object>}} csvData
 * @returns {{isValid: boolean, errors: Array<string>,
 * units: Array<{serialNumber: ?string}>}}
 */
export const validatePowerBankCsv = csvData => {
  const errors = []
  const units = []
  const rows = (csvData && csvData.data) || []
  const serialTracker = new Map()

  const hasSerialColumn = rows.some(
    row => row && Object.prototype.hasOwnProperty.call(row, 'serialNumber'),
  )
  if (rows.length && !hasSerialColumn) {
    return {
      isValid: false,
      errors: [
        'Row 1: The file must have a "serialNumber" column. Download the template to see the expected format.',
      ],
      units: [],
    }
  }

  rows.forEach((row, index) => {
    if (isBlankRow(row)) return

    const rowNumber = index + 2 // 1-based, after the header row
    const serial = cleanCell(row.serialNumber)
    const quantityCell = cleanCell(row.quantity)

    if (serial) {
      if (serial.length > MAX_SERIAL_LENGTH) {
        errors.push(
          `Row ${rowNumber}: Serial number is longer than ${MAX_SERIAL_LENGTH} characters`,
        )
      }
      if (quantityCell && quantityCell !== '1') {
        errors.push(
          `Row ${rowNumber}: A row with a serial number is one unit, leave quantity empty or set it to 1`,
        )
      }
      const key = serial.toUpperCase()
      if (serialTracker.has(key)) {
        errors.push(
          `Row ${rowNumber}: Serial number "${serial}" is already on row ${serialTracker.get(
            key,
          )}`,
        )
      } else {
        serialTracker.set(key, rowNumber)
        units.push({ serialNumber: serial })
      }
      return
    }

    const quantity = quantityCell === '' ? 1 : Number(quantityCell)
    if (!Number.isInteger(quantity) || quantity < 1) {
      errors.push(
        `Row ${rowNumber}: Quantity must be a whole number of 1 or more when there is no serial number`,
      )
      return
    }
    for (let i = 0; i < quantity; i += 1) units.push({ serialNumber: null })
  })

  if (!errors.length && units.length === 0) {
    errors.push('Row 2: The file has no power banks in it')
  }

  if (units.length > MAX_POWER_BANK_UNITS_PER_UPLOAD) {
    errors.push(
      `Row 1: The import is limited to ${MAX_POWER_BANK_UNITS_PER_UPLOAD} power banks per file, this file has ${
        units.length
      }`,
    )
  }

  return { isValid: errors.length === 0, errors, units }
}

/**
 * Parses what a city manager typed into a hold amount box.
 * @param {string|number} input
 * @returns {{value: ?number, error: ?string}}
 */
export const parseHoldAmountInput = input => {
  const text = cleanCell(input).replace(',', '.')
  if (text === '') return { value: null, error: 'Enter a hold amount' }

  const value = Number(text)
  if (!Number.isFinite(value)) {
    return { value: null, error: 'The hold amount must be a number' }
  }
  if (value <= 0) {
    return { value: null, error: 'The hold amount must be more than zero' }
  }
  return { value: Math.round(value * 100) / 100, error: null }
}

/**
 * Renders money with the city's currency sign in front ("₪120.00"), falling
 * back to the ISO code behind it ("120.00 ILS") when no sign is known.
 * @param {number|string} amount
 * @param {{currencySign: ?string, currency: ?string}} money
 * @returns {string}
 */
export const formatHoldAmount = (amount, { currencySign, currency } = {}) => {
  const value = Number(amount)
  const text = Number.isFinite(value) ? value.toFixed(2) : '0.00'
  if (currencySign) return `${currencySign}${text}`
  if (currency) return `${text} ${currency}`
  return text
}

/**
 * True when a hold actually holds money. A zero hold row is the paper trail
 * of a handover with no deposit, and must not read as a deposit.
 * @param {?{holdAmount: ?number, holdStatus: ?string}} unit
 * @returns {boolean}
 */
export const unitHasLiveHold = unit =>
  Boolean(unit) &&
  unit.holdStatus === POWER_BANK_HOLD_STATUS.PLACED &&
  Number(unit.holdAmount) > 0

/**
 * One line describing a unit's hold for a table cell.
 * @param {{holdAmount: ?number, holdCurrency: ?string, holdStatus: ?string}} unit
 * @param {{currencySign: ?string}} money
 * @returns {string}
 */
export const describeUnitHold = (unit, money = {}) => {
  if (!unitHasLiveHold(unit)) return 'No hold'
  return formatHoldAmount(unit.holdAmount, {
    currencySign: money.currencySign,
    currency: unit.holdCurrency,
  })
}

/**
 * Whether and when the BA agreed to a unit's hold from the app, for a table
 * cell. A unit with no money held has nothing to agree to.
 * @param {{holdAmount: ?number, holdStatus: ?string, holdAcceptedAt: ?string}} unit
 * @returns {string}
 */
export const describeUnitAgreement = unit => {
  if (!unitHasLiveHold(unit)) return '—'
  if (!unit.holdAcceptedAt) return 'Awaiting agreement in the app'
  return `Agreed ${new Date(unit.holdAcceptedAt).toLocaleDateString('en-GB')}`
}

/**
 * The pickup page's line under a BA's chips: the amount held for one unit
 * and whether the BA agreed to it in the app.
 * @param {?{amount: ?number, currency: ?string, acceptedAt: ?string}} hold
 * @param {{currencySign: ?string}} money
 * @returns {{amount: string, agreement: ?{className: string, text: string}}}
 */
export const describeHoldLine = (hold, money = {}) => {
  if (!hold || !(Number(hold.amount) > 0)) {
    return { amount: 'No hold', agreement: null }
  }
  const amount = formatHoldAmount(hold.amount, {
    currencySign: money.currencySign,
    currency: hold.currency,
  })
  const agreement = hold.acceptedAt
    ? {
        className: 'agreed',
        text: `Agreed ${new Date(hold.acceptedAt).toLocaleDateString('en-GB')}`,
      }
    : { className: 'awaiting', text: 'Awaiting BA agreement' }
  return { amount, agreement }
}

/**
 * What the inventory page's deposit card says for a city's setting.
 * @param {?{holdRequired: boolean, holdAmount: ?number, currency: ?string}} setting
 * @param {{currencySign: ?string}} money
 * @returns {{amount: string, detail: string}}
 */
export const describeHoldSetting = (setting, money = {}) => {
  if (!setting || !setting.holdRequired || !(Number(setting.holdAmount) > 0)) {
    return {
      amount: 'No deposit',
      detail:
        'Power banks are handed over without a hold. You can still enter one on a single handover.',
    }
  }
  return {
    amount: formatHoldAmount(setting.holdAmount, {
      currencySign: money.currencySign,
      currency: setting.currency,
    }),
    detail:
      'Held from the brand ambassador for each power bank, deducted from their pay and returned when the unit comes back. Offered on every handover and editable there.',
  }
}

/**
 * Sorts units by serial so the pickup dropdown and the profile table are
 * stable between reloads.
 * @param {Array<{description: ?string}>} units
 * @returns {Array}
 */
export const sortUnitsBySerial = units =>
  (units || []).slice().sort((a, b) =>
    String(a.description || '').localeCompare(
      String(b.description || ''),
      undefined,
      {
        numeric: true,
      },
    ),
  )
